import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Container } from "reactstrap";
import styled from "styled-components";
import NewFooter from "../../components/Footers/NewFooter";
import NewNavbar from "../../components/Navbar/NewNavbar";
import {
  CREDITS_PACK_KEY,
  PACKS,
  PLANS,
  PLAN_LABELS,
  clearCreditsReturn,
  formatCredits,
  formatCreditsNumber,
  planProduct,
  readCreditsReturn,
  stashCreditsResume,
} from "../../constants/credits";
import { useAccount } from "../../contexts/AccountContext";
import { track } from "../../utils/analytics";
import { storageGet, storageRemove } from "../../utils/storage";

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
`;

const ContentWrapper = styled.div`
  flex: 1;
  padding: 120px 0 60px;
  display: flex;
  align-items: center;
`;

const SuccessCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 60px 40px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  max-width: 700px;
  margin: 0 auto;
  text-align: center;

  @media (max-width: 768px) {
    padding: 40px 24px;
    margin: 0 16px;
  }
`;

const SuccessIcon = styled.div`
  width: 80px;
  height: 80px;
  background: #10b981;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 32px;
  font-size: 40px;
  color: white;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const Message = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  color: #4a4a4a;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const SupportMessage = styled.p`
  font-size: 1rem;
  color: #6b7280;
  margin-top: 32px;

  a {
    color: #1a1a1a;
    font-weight: 600;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const DashboardCta = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  margin-top: 24px;
  padding: 0 28px;
  border-radius: 999px;
  font-size: 1rem;
  font-weight: 700;
  text-decoration: none;
  border: 1px solid #0e7e7a;
  background: #0e7e7a;
  color: #ffffff;

  &:hover {
    background: #0b605d;
    color: #ffffff;
  }
`;

const Support = () => (
  <SupportMessage>
    If you have any questions, please reach out to{" "}
    <a href="mailto:help@listino.ai">help@listino.ai</a>
  </SupportMessage>
);

/**
 * The credits return legs. Three arrivals share this page:
 *  - /success?purchase=pack           Stripe, after a credit pack
 *  - /success?listing_uuid=…          the Prepare sheet, after a debit went through
 *  - /subscription-success            Stripe, after a plan
 * Offers one way onward: back to where the purchase started (the stashed
 * return path), the specific listing, or the listings dashboard. Query
 * parameters identify a destination, not proof of a debit or job status.
 * A pack bought from a blocked tap also hands the interrupted action back to
 * that page (the resume stash), so it is offered again without re-entering it.
 *
 * @param {{ subscription?: boolean }} props
 */
const PaymentSuccess = ({ subscription = false }) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const listingIds = params.getAll("listing_uuid");
  const listingUuid =
    listingIds.length === 1 &&
    listingIds[0].length === 36 &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(listingIds[0])
      ? listingIds[0].toLowerCase()
      : null;
  return (
    <CreditsReturn
      subscription={subscription}
      isPack={!subscription && params.get("purchase") === "pack"}
      listingUuid={listingUuid}
    />
  );
};

/** @param {{ subscription: boolean, isPack: boolean, listingUuid: string | null }} props */
const CreditsReturn = ({ subscription, isPack, listingUuid }) => {
  const { credits, loading, refresh, signedIn } = useAccount();
  // A guest plan return carries ?plan=agent|pro from the checkout's success
  // URL — the only way to name the plan without a session.
  const planHint = new URLSearchParams(useLocation().search).get("plan") || "";
  const planHintLabel = PLAN_LABELS[planHint] || "";
  // One-shot reads: the pack key and the return stash are consumed here so a
  // later, unrelated visit never reuses them.
  const [packKey] = useState(() => {
    const key = storageGet(CREDITS_PACK_KEY);
    if (key) storageRemove(CREDITS_PACK_KEY);
    return key;
  });
  const [returnStash] = useState(() => {
    const stash = readCreditsReturn();
    if (stash) clearCreditsReturn();
    return stash;
  });
  const returnPath = returnStash?.path || null;
  // Pack returns belong to the interrupted purchase, even if their URL also
  // contains a listing id. Opening a listing only reads authorized status;
  // it must never acknowledge a paid attempt or restart preparation.
  const listingPath = !isPack && listingUuid ? `/properties/${listingUuid}` : null;
  const trackedRef = useRef(false);

  // The balance shown is the server's, read AFTER the webhook granted the
  // credits — never a local guess. The event fires once, from the fresh read.
  useEffect(() => {
    if (trackedRef.current) return;
    trackedRef.current = true;
    if (isPack) {
      const pack = packKey ? PACKS[packKey] : null;
      track("credits_pack_purchased", {
        pack: packKey || "unknown",
        credits: pack?.credits ?? 0,
      });
    }
    // The interrupted action goes back to its page, so the page can reopen
    // its confirm with "Credits added — you have N". Stashed at once (a fast
    // Continue must not lose it), then again with the fresh balance.
    const resumeStash =
      isPack && returnStash?.resume && signedIn
        ? {
            resume: returnStash.resume,
            ...(returnStash.listingUuid ? { listingUuid: returnStash.listingUuid } : {}),
          }
        : null;
    if (resumeStash) stashCreditsResume({ ...resumeStash, balance: null });
    refresh().then((data) => {
      if (subscription) {
        track("credits_plan_purchased", {
          plan_key: data?.credits?.plan_key || "unknown",
        });
      }
      if (resumeStash) {
        const balance = Number(data?.credits?.balance);
        stashCreditsResume({
          ...resumeStash,
          balance: Number.isFinite(balance) ? balance : null,
        });
      }
    });
  }, [isPack, packKey, subscription, refresh, returnStash, signedIn]);

  const balanceKnown = !!credits;
  let title;
  let message;
  let ctaTo = "/dashboard";
  let ctaLabel = "Open your listings";

  // A guest purchase (Trello #53): Stripe took the email, the webhook made the
  // account, and the receipt says to sign in with that address. No session
  // here means no balance to read — the page states the fact and the way in.
  const guestPurchase = !signedIn && (subscription || isPack);

  if (guestPurchase) {
    title = "You're all set";
    const what = subscription
      ? `${planHintLabel ? `${planHintLabel} ` : ""}plan is`
      : "credits are";
    message = `Your ${what} ready. We emailed your receipt to the address you used at checkout — sign in with that address to start.`;
    ctaTo = "/login";
    ctaLabel = "Sign in";
    if (listingPath) ctaTo += `?next=${encodeURIComponent(listingPath)}`;
  } else if (subscription) {
    const planKey = credits?.plan_key || "";
    const plan = PLANS[planKey];
    const label = PLAN_LABELS[planProduct(planKey)];
    title = "You're subscribed";
    if (plan) {
      message = `${label} plan active — ${formatCredits(plan.credits)} added.`;
    } else if (loading && !credits) {
      message = "Confirming your plan…";
    } else {
      message = "Your plan is active.";
    }
    if (listingPath) {
      message += " Open this listing to check preparation progress and available results.";
      ctaTo = listingPath;
      ctaLabel = "Open this listing";
    } else if (returnPath) {
      ctaTo = returnPath;
      ctaLabel = "Continue";
    }
  } else if (isPack) {
    const pack = packKey ? PACKS[packKey] : null;
    title = "Credits added";
    const added = pack ? `${formatCredits(pack.credits)} added` : "Credits added";
    message = balanceKnown
      ? `${added} — you have ${formatCreditsNumber(credits.balance)}.`
      : `${added}.`;
    ctaLabel = "Continue";
    if (returnPath) ctaTo = returnPath;
  } else {
    title = "Your listing workspace";
    message = listingPath
      ? "Follow preparation progress and open your analysis and purchased materials from this listing's workspace."
      : "Open your listings to check preparation progress and available results.";
    if (listingPath) {
      ctaTo = signedIn ? listingPath : `/login?next=${encodeURIComponent(listingPath)}`;
      ctaLabel = signedIn ? "Open this listing" : "Sign in to open this listing";
    }
  }

  return (
    <PageContainer>
      <NewNavbar />
      <ContentWrapper>
        <Container>
          <SuccessCard>
            <SuccessIcon>✓</SuccessIcon>
            <Title>{title}</Title>
            <Message>{message}</Message>
            <DashboardCta as={Link} to={ctaTo}>
              {ctaLabel}
            </DashboardCta>
            <Support />
          </SuccessCard>
        </Container>
      </ContentWrapper>
      <NewFooter />
    </PageContainer>
  );
};

export default PaymentSuccess;
